import { NavLink } from "react-router";

import { ChevronRight } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  SxProps,
  Theme,
  Typography,
  useMediaQuery
} from "@mui/material";

import { darkModeLinkColor, isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { theme } from "@/src/theme/theme";

export type Breadcrumb = {
  id: string;
  label: string;
  url?: string;
};

interface BreadcrumbPanelProps {
  crumbs: Breadcrumb[];
  sx?: SxProps;
}

const separatorSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  fontSize: muiTheme.typography.body1.fontSize,
  color: isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : muiTheme.palette.text.secondary
}));

// A crumb points at a page the reader has almost always just come from, so it
// takes the unvisited link color alone: the followed state would be permanent
// and would say nothing useful.
const linkCrumbSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  fontWeight: muiTheme.typography.fontWeightRegular,
  cursor: "pointer",
  textDecoration: "underline",
  ...(isDarkMode(muiTheme) ? { color: darkModeLinkColor(muiTheme) } : {})
}));

const currentCrumbSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  fontWeight: muiTheme.typography.fontWeightBold,
  ...(isDarkMode(muiTheme) ? { color: muiTheme.palette.text.primary } : {})
}));

const BreadcrumbPanel = (props: BreadcrumbPanelProps) => {
  const { crumbs, sx } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        ml: isMobile ? 0 : 4,
        mt: isMobile ? 2 : 4,
        mb: isMobile ? 1 : 0,
        a: {
          textDecoration: "none"
        },
        alignItems: "center",
        ...sx
      }}
    >
      <Breadcrumbs separator={<ChevronRight sx={separatorSx} />}>
        {crumbs.map((crumb: Breadcrumb) => {
          if (crumb.url) {
            return (
              <NavLink id={crumb.id} key={crumb.id} to={crumb.url}>
                <Typography
                  id={crumb.id}
                  key={crumb.id}
                  variant="caption"
                  color="primary"
                  sx={linkCrumbSx}
                >
                  {crumb.label}
                </Typography>
              </NavLink>
            );
          }
          return (
            <Typography
              id={crumb.id}
              key={crumb.id}
              variant="caption"
              color="secondary"
              sx={currentCrumbSx}
            >
              {crumb.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default BreadcrumbPanel;
